import React, { useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Typography,
} from '@mui/material';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import { useSnackbar } from 'notistack';
import ConfirmCheckInDialog from '../components/ConfirmCheckInDialog';
import { getAvailableRooms, getSharingTypes } from '../checkInService';

const AVAILABLE_STATUS = 'AVAILABLE';

function getInitialCheckInDetails() {
  return {
    checkInDate: '',
    checkInType: '',
    rentCollectionDay: '',
    numberOfWeeksDays: '',
  };
}

function normalizeSharingTypes(sharingTypes = []) {
  if(!Array.isArray(sharingTypes)){
    return [];
  }

  return sharingTypes.map(type => {
    const rawValue = String(type).trim();
    const value = rawValue.match(/^\d+/)?.[0] || rawValue;

    if(value === '1'){
      return {
        value,
        label: 'Private',
      };
    }

    return {
      value,
      label: rawValue.toLowerCase().includes('sharing') ? rawValue : `${value} - Sharing`,
    };
  });
}

function normalizeAvailableRooms(rooms = []) {
  if(!Array.isArray(rooms)){
    return [];
  }

  const roomsById = new Map();

  rooms.forEach((room, index) => {
    const roomKey = room.roomId ?? room.roomNumber ?? room.id ?? `Room ${index + 1}`;
    const existingRoom = roomsById.get(roomKey);
    const bed = {
      bedId: room.bedId ?? null,
      bedNumber: room.bedNumber ?? '',
      status: room.status || AVAILABLE_STATUS,
      raw: room,
    };

    if(existingRoom){
      existingRoom.availableBeds.push(bed);
      existingRoom.beds = existingRoom.availableBeds.length;
      return;
    }

    roomsById.set(roomKey, {
      id: room.roomNumber ?? room.id ?? room.name ?? `Room ${index + 1}`,
      roomId: room.roomId ?? room.id ?? null,
      block: room.block || 'Block',
      floor: room.floor || '',
      status: room.status || AVAILABLE_STATUS,
      beds: 1,
      availableBeds: [bed],
      raw: room,
    });
  });

  return Array.from(roomsById.values());
}

function getRoomKey(room) {
  return room.roomId ?? room.id;
}

function getRoomBedLabel(room) {
  if(room.beds === 1){
    return '1 bed available';
  }

  return `${room.beds} beds available`;
}

function getSelectedRoomTypeLabel(roomTypes, roomType) {
  const selectedRoomType = roomTypes.find(type => type.value === roomType);
  return selectedRoomType?.label || 'Select sharing type';
}

function formatLocationPart(prefix, value) {
  return String(value).toLowerCase().startsWith(prefix.toLowerCase()) ? value : `${prefix} ${value}`;
}

function getRoomLocation(room) {
  const locationParts = [];

  if(room.block){
    locationParts.push(formatLocationPart('Block', room.block));
  }

  if(room.floor){
    locationParts.push(formatLocationPart('Floor', room.floor));
  }

  return locationParts.join(', ') || 'Room location unavailable';
}

function CheckInStep({ pgId, tenantUuid, rentCollectionType, onSubmitSuccess, pgDefaults }) {
  const { enqueueSnackbar } = useSnackbar();
  const [roomType, setRoomType] = useState('');
  const [roomTypes, setRoomTypes] = useState([]);
  const [availableRooms, setAvailableRooms] = useState([]);
  const [isLoadingRooms, setIsLoadingRooms] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [checkInDetails, setCheckInDetails] = useState(getInitialCheckInDetails);

  const loadAvailableRooms = (sharingType) => {
    setRoomType(sharingType);
    setAvailableRooms([]);

    if(!pgId || !sharingType){
      return;
    }

    setIsLoadingRooms(true);

    getAvailableRooms(pgId, sharingType, AVAILABLE_STATUS).then(({response, error}) => {
      setIsLoadingRooms(false);

      if(error){
        enqueueSnackbar(error?.message || 'Unable to load available rooms', {variant: 'error'});
        return;
      }

      const rooms = response?.data || response || [];
      setAvailableRooms(normalizeAvailableRooms(rooms));
    });
  };

  useEffect(() => {
    if(!pgId){
      return;
    }

    getSharingTypes(pgId).then(({response, error}) => {
      if(error){
        enqueueSnackbar(error?.message || 'Unable to load sharing types', {variant: 'error'});
        return;
      }

      const sharingTypes = response?.data || response || [];
      const apiRoomTypes = normalizeSharingTypes(sharingTypes);

      setRoomTypes(apiRoomTypes);
      setRoomType('');
    });
  }, [enqueueSnackbar, pgId]);

  const openConfirmation = (room) => {
    setSelectedRoom(room);
    setCheckInDetails(getInitialCheckInDetails());
    setConfirmOpen(true);
  };

  const closeConfirmation = () => {
    setConfirmOpen(false);
  };

  const handleCheckInSubmitSuccess = () => {
    setConfirmOpen(false);
    setRoomType('');
    setAvailableRooms([]);
    setSelectedRoom(null);
    setCheckInDetails(getInitialCheckInDetails());
    onSubmitSuccess?.();
  };

  return (
    <Box>
      <FormControl fullWidth sx={{ mb: 4 }}>
        <InputLabel id="room-type-label">Room Type</InputLabel>
        <Select
          labelId="room-type-label"
          label="Room Type"
          value={roomType}
          onChange={(event) => loadAvailableRooms(event.target.value)}
          displayEmpty
          renderValue={(selected) => getSelectedRoomTypeLabel(roomTypes, selected)}
          startAdornment={
            <InputAdornment position="start">
              <HomeWorkIcon color="disabled" />
            </InputAdornment>
          }
        >
          <MenuItem value="" disabled>Select sharing type</MenuItem>
          {roomTypes.map(type => <MenuItem value={type.value} key={type.value}>{type.label}</MenuItem>)}
        </Select>
      </FormControl>

      {isLoadingRooms && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!roomType && !isLoadingRooms && (
        <Box
          sx={{
            py: 6,
            px: 3,
            borderRadius: 4,
            border: '1px dashed',
            borderColor: 'primary.light',
            bgcolor: 'primary.light',
            color: 'common.white',
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              mx: 'auto',
              mb: 2,
              borderRadius: '50%',
              bgcolor: 'rgba(255,255,255,0.18)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
            }}
          >
            <HomeWorkIcon sx={{ color: 'common.white' }} />
          </Box>
          <Typography variant="h6" fontWeight={800}>
            Choose a sharing type
          </Typography>
          <Typography sx={{ mt: 1, color: 'rgba(255,255,255,0.82)' }}>
            Available rooms will appear here once you pick a room type.
          </Typography>
        </Box>
      )}

      {roomType && !isLoadingRooms && availableRooms.length === 0 && (
        <Box
          sx={{
            py: 5,
            px: 3,
            borderRadius: 4,
            bgcolor: 'grey.100',
            textAlign: 'center',
          }}
        >
          <Typography variant="h6" fontWeight={800}>
            No rooms available
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            Try another sharing type to see available rooms.
          </Typography>
        </Box>
      )}

      {roomType && !isLoadingRooms && availableRooms.length > 0 && (
        <Grid container spacing={3}>
          {availableRooms.map(room => (
            <Grid item xs={12} sm={6} key={getRoomKey(room)}>
              <Paper
                elevation={0}
                onClick={() => openConfirmation(room)}
                sx={{
                  p: 3,
                  minHeight: 150,
                  borderRadius: 4,
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'grey.200',
                  cursor: 'pointer',
                  transition: 'transform 160ms ease, box-shadow 160ms ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.12)',
                  },
                }}
              >
                <Typography fontWeight={800} sx={{ color: 'primary.dark', mb: 2 }}>{room.id}</Typography>
                <Typography fontWeight={700}>{getRoomLocation(room)}</Typography>
                <Typography fontWeight={700} sx={{ mt: 1 }}>{getRoomBedLabel(room)}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}

      <ConfirmCheckInDialog
        open={confirmOpen}
        pgId={pgId}
        tenantUuid={tenantUuid}
        selectedRoom={selectedRoom}
        checkInDetails={checkInDetails}
        sharingType={roomType}
        rentCollectionType={rentCollectionType}
        onClose={closeConfirmation}
        onDetailsChange={setCheckInDetails}
        onSubmitSuccess={handleCheckInSubmitSuccess}
        pgDefaults={pgDefaults}
      />
    </Box>
  );
}

export default CheckInStep;
